import { useFormik } from 'formik';
import _debounce from 'lodash/debounce';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

// Components
import RadioGroup from '../components/pageComponents/Custom/RadioGroup';
import FormField from '../components/pageComponents/EditProfile/components/FormField';
import Header from '../components/reusable/Header';
import SelectWithPicture from '../components/reusable/SelectWithPicture';
import BackButton from '../components/reusable/buttons/BackButton';
import { BigButtons } from '../components/reusable/buttons/Buttons';

import { dispatch } from '../redux/store';

// Utils
import moment from 'moment';
import { notifyError, removeEmpty } from '../utils/helpers';

// Contract status options matching the design
const CONTRACT_STATUS_OPTIONS = [
    { value: 'UNSIGNED', label: 'Unsigned' },
    { value: 'CONTRACT', label: 'Contract' },
    { value: 'NON_CONTRACT', label: 'Non-Contract' }
];

// Validation schema
const contractValidationSchema = yup.object().shape({
    contractStatus: yup.string().required('Please select a contract status'),
    clubId: yup.string().when('contractStatus', {
        is: val => val === 'CONTRACT' || val === 'NON_CONTRACT',
        then: () => yup.string().required('Please select a football club'),
        otherwise: () => yup.string().notRequired()
    }),
    teamId: yup.string().when('clubId', {
        is: val => Boolean(val),
        then: () => yup.string().required('Please select a team'),
        otherwise: () => yup.string().notRequired()
    }),
    contractExpiry: yup.string().when('contractStatus', {
        is: val => val === 'CONTRACT',
        then: () =>
            yup.string().required('Please select a contract expiry date'),
        otherwise: () => yup.string().notRequired()
    })
});

const ContractSelect = () => {
    const navigate = useNavigate();
    const userInfo = useSelector(({ auth }) => auth?.authUser?.userInfo);

    // Club and team selection state
    const [teams, setTeams] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [selectTeam, setSelectTeam] = useState({});
    const [selectedClub, setSelectedClub] = useState({});
    const [errorMessage, setNotFoundClubError] = useState(false);
    const [isFetchingTeams, setIsFetchingTeams] = useState(false);

    // Loading state for user update
    const loadingUserUpdate = useSelector(
        ({ loading }) => loading.effects.user.updateUser
    );

    // Formik setup
    const formik = useFormik({
        initialValues: {
            contractStatus: userInfo?.contractStatus || 'UNSIGNED',
            clubId: userInfo?.clubId || '',
            teamId: userInfo?.teamId || '',
            contractExpiry: userInfo?.contractExpiry || ''
        },
        validationSchema: contractValidationSchema,
        validateOnMount: false,
        validateOnChange: false,
        validateOnBlur: true,
        onSubmit: values => {
            handleFormSubmit(values);
        }
    });

    const {
        values,
        errors,
        touched,
        dirty,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue
    } = formik;

    const handleTeamChange = useCallback(
        ({ target }) => {
            const selectedTeam =
                teams.find(item => item.id === target.value) || {};

            setSelectTeam(selectedTeam);
            setFieldValue('teamId', target.value);
        },
        [teams, setFieldValue]
    );

    const handleClubSelection = useCallback(
        async clubId => {
            setIsFetchingTeams(true);
            const teamsInGivenClub = [];
            const res = await dispatch.team.getTeamsByClubId(clubId);

            if (res?.data) {
                const filteredTeams = res.data.filter(
                    team => team.clubGender === userInfo?.gender
                );
                teamsInGivenClub.push(...filteredTeams);
            }

            if (teamsInGivenClub?.length === 1) {
                setSelectTeam(teamsInGivenClub[0]);
                setFieldValue('teamId', teamsInGivenClub[0]?.id);
            }
            setTeams(teamsInGivenClub);
            setIsFetchingTeams(false);
        },
        [setFieldValue, userInfo?.gender]
    );

    const getTeamsBasedOnSelectedClub = useCallback(
        _debounce(handleClubSelection, 100),
        [handleClubSelection]
    );

    const handleFormSubmit = useCallback(
        async values => {
            try {
                // Prepare data for submission
                const mergedValues = {
                    ...values,
                    id: userInfo.id,
                    teamId: selectTeam?.id || '',
                    clubId: selectedClub?.id || ''
                };

                // Clean empty values
                const cleanData = removeEmpty(mergedValues);

                // Submit the form data
                const res = await dispatch.user.updateUser(cleanData);

                if (res === 1) {
                    // Refresh user data
                    await Promise.all([
                        dispatch.user.getUserProfile({ id: userInfo?.id }),
                        dispatch.user.userSearchByProjection()
                    ]);

                    navigate('/account-setup');
                }
            } catch (error) {
                console.error('Contract submission error:', error);
                notifyError(
                    'Failed to update contract information. Please try again.'
                );
            }
        },
        [
            dirty,
            userInfo,
            selectTeam,
            selectedClub,
            teams,
            errorMessage,
            searchValue,
            navigate
        ]
    );

    // Update club name in formik when club is selected
    useEffect(() => {
        if (selectedClub?.id) {
            setFieldValue('clubId', selectedClub.id);
            handleBlur({
                target: { name: 'clubId' }
            });
        } else if (!selectedClub?.id && values.clubId) {
            // Clear clubId if no club is selected
            setFieldValue('clubId', '');
        }
    }, [selectedClub, setFieldValue, values.clubId]);

    // Update team name in formik when team is selected
    useEffect(() => {
        if (selectTeam?.id) {
            setFieldValue('teamId', selectTeam.id);
            handleBlur({
                target: { name: 'teamId' }
            });
        } else if (!selectTeam?.id && values.teamId) {
            // Clear teamName if no team is selected
            setFieldValue('teamId', '');
        }
    }, [selectTeam, setFieldValue, values.teamId]);

    useEffect(() => {
        if (values.contractStatus === 'UNSIGNED') {
            setFieldValue('clubId', '');
            setFieldValue('teamId', '');
            setFieldValue('contractExpiry', '');
            setSelectedClub({});
            setSelectTeam({});
            setTeams([]);
        } else if (values.contractStatus === 'NON_CONTRACT') {
            setFieldValue('contractExpiry', '');
        }
    }, [values.contractStatus, setFieldValue]);

    const handleBack = () => {
        navigate(-1);
    };

    // Initialize with existing user data
    useEffect(() => {
        // Initialize selected club if exists in userInfo
        if (userInfo?.clubId) {
            setSelectedClub({
                id: userInfo.clubId,
                clubName: userInfo.clubName
            });
        }

        // Initialize selected team if exists in userInfo
        if (userInfo?.teamId) {
            setSelectTeam({
                id: userInfo.teamId,
                teamName: userInfo.teamName
            });
        }
    }, [userInfo]);

    return (
        <div className='min-h-screen bg-white page'>
            {/* Header with back button */}
            <Header>
                <BackButton onClick={handleBack} />
                <div></div> {/* Empty div for spacing */}
            </Header>

            <form onSubmit={handleSubmit}>
                <div className='px-2 pb-32'>
                    {/* Page Title */}
                    <div className='mb-8'>
                        <h1 className='text-[18px] font-bold text-black uppercase  mb-4 font-favela-bold'>
                            CONTRACT STATUS
                        </h1>
                        <p className='text-[14px] text-gray-700 leading-relaxed font-poppins'>
                            What is your current contract status. Please select
                            the option that best applies to you.
                        </p>
                    </div>

                    {/* Contract Status Selection */}
                    <div className='mb-12'>
                        <RadioGroup
                            name='contractStatus'
                            value={values.contractStatus}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            options={CONTRACT_STATUS_OPTIONS}
                            direction='col'
                            className='space-y-4'
                            size='lg'
                        />
                        {errors.contractStatus && touched.contractStatus && (
                            <p className='text-red-600 text-xs mt-2'>
                                {errors.contractStatus}
                            </p>
                        )}
                    </div>

                    {/* Conditional sections based on contract status */}
                    {(values.contractStatus === 'CONTRACT' ||
                        values.contractStatus === 'NON_CONTRACT') && (
                        <>
                            {/* Football Club Section */}
                            <div className='relative w-full mb-7 group'>
                                <label
                                    htmlFor='clubs'
                                    className={`text-[14px] ${errors.clubId && touched.clubId ? 'text-red-600' : 'text-gray-500'}`}
                                >
                                    Football Club
                                </label>
                                <div className='flex items-center relative'>
                                    <div className='appearance-none border-none w-full text-gray-700 py-1 leading-tight'>
                                        <SelectWithPicture
                                            gender={
                                                userInfo?.gender === 'BOTH'
                                                    ? undefined
                                                    : userInfo?.gender
                                            }
                                            types={
                                                userInfo?.userType !==
                                                'NON_PLAYER'
                                                    ? ['DOMESTIC']
                                                    : undefined
                                            }
                                            setSelectClub={setSelectedClub}
                                            selectClub={selectedClub}
                                            setTeams={setTeams}
                                            borderColor={
                                                errors.clubId && touched.clubId
                                                    ? 'border-red-600'
                                                    : ''
                                            }
                                            setSelectTeam={setSelectTeam}
                                            setNotFoundClubError={
                                                setNotFoundClubError
                                            }
                                            searchValue={searchValue}
                                            setSearchValue={setSearchValue}
                                            getTeamsBasedOnSelectedClub={
                                                getTeamsBasedOnSelectedClub
                                            }
                                            onBlur={() =>
                                                handleBlur({
                                                    target: { name: 'clubId' }
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                                {errors.clubId &&
                                    touched.clubId &&
                                    !searchValue && (
                                        <p className='text-red-600 text-xs'>
                                            {errors.clubId}
                                        </p>
                                    )}
                            </div>

                            {/* Team Section */}
                            {!!teams?.length && (
                                <div className='relative w-full my-5'>
                                    <label
                                        htmlFor='teamId'
                                        className={`text-[14px] ${errors.teamId ? 'text-red-600' : 'text-gray-500'}`}
                                    >
                                        Team
                                    </label>
                                    <div className='flex items-center'>
                                        <div className='appearance-none border-none w-full text-gray-700 py-1 leading-tight'>
                                            <select
                                                name='teamId'
                                                value={values.teamId}
                                                onChange={handleTeamChange}
                                                onBlur={handleBlur}
                                                className={`appearance-none bg-transparent border-b-2 ${errors.teamId ? 'border-red-600' : 'border-gray-600'} w-full ${errors.teamId ? 'text-red-600' : 'text-gray-700'} py-1 pl-2 leading-tight focus:outline-none`}
                                            >
                                                <option value=''>
                                                    Select team
                                                </option>
                                                {teams.map(team => (
                                                    <option
                                                        key={team.id}
                                                        value={team?.id}
                                                    >
                                                        {team?.teamName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    {errors.teamId && touched.teamId && (
                                        <p className='text-red-600 text-xs mt-1'>
                                            {errors.teamId}
                                        </p>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {/* Contract Expiry Section - Only show for CONTRACT status */}
                    {values.contractStatus === 'CONTRACT' && (
                        <FormField
                            name='contractExpiry'
                            label='Contract Expiry Date'
                            type='month'
                            value={
                                moment(values?.contractExpiry).isValid()
                                    ? values.contractExpiry
                                    : ''
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder='Select Contract Expiry'
                            error={errors.contractExpiry}
                            touched={touched.contractExpiry}
                        />
                    )}
                </div>

                {/* Fixed Next Button at bottom */}
                <div className='fixed bottom-0 left-0 right-0 p-4'>
                    <BigButtons
                        black
                        label={loadingUserUpdate ? 'SAVING...' : 'NEXT'}
                        type='submit'
                        isDisabled={isFetchingTeams || loadingUserUpdate}
                        isLoading={loadingUserUpdate}
                    />
                </div>
            </form>
        </div>
    );
};

export default ContractSelect;
